import type { ErrorEvent as SentryErrorEvent } from '@sentry/types';

/**
 * Gives a name to an HTTP failure that escaped without being caught.
 *
 * WHAT ARRIVES WITHOUT THIS. An Angular HttpErrorResponse is not an Error: it
 * has no stack and no name that Sentry recognises. When one escapes to the
 * window handler, Sentry does the only thing it can and reports
 *
 *   Object captured as exception with keys: error, headers, message, name, ok
 *
 * with a stack pointing at polyfills.js line 1. That title says nothing about
 * which call failed, and worse, it is the SAME title for every unhandled HTTP
 * failure in the application: a 401 on the profile, a 500 on a group, a
 * timeout on a payment all land in one bucket that cannot be read, ranked or
 * acted on. CAPACITOR-T is six of those, and the reason its call site could
 * not be found is that the report never carried one.
 *
 * WHAT ARRIVES WITH IT. The status and the endpoint, in the title, and a
 * grouping that keeps different endpoints apart:
 *
 *   HttpErrorResponse: 401 on /api/v1/profile
 *
 * HANDLED FAILURES ARE UNTOUCHED. Only what escapes reaches here in this
 * shape. An error reported deliberately through captureException already
 * carries its own type and stack, and nothing below matches it.
 *
 * NOTHING NEW IS SENT. The endpoint is already inside the event, in the
 * serialised payload Sentry attached itself. This moves it into the title and
 * the fingerprint, and on the way REMOVES identifiers from the path, so that
 * every group id stops making an issue of its own and no id travels in a tag.
 */

/** The fields of an HttpErrorResponse that decide anything. */
type TheSerialisedFailure = {
  readonly name?: unknown;
  readonly status?: unknown;
  readonly url?: unknown;
};

const looksLikeAnHttpFailure = (serialised: TheSerialisedFailure | undefined): boolean =>
  serialised?.name === 'HttpErrorResponse';

/**
 * The path of a URL, without its origin, its query string, or any identifier.
 *
 * Identifiers are replaced rather than kept: a Mongo object id or a numeric id
 * in the path would make one Sentry issue per group, per activity, per member,
 * which is the same as having none. It also keeps ids out of the tags.
 */
export const thePathThatFailed = (url: unknown): string => {
  if (typeof url !== 'string' || url.trim() === '') {
    return 'unknown';
  }

  let path = url.trim();
  path = path.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '');
  path = path.split(/[?#]/)[0];
  if (path === '') {
    return 'unknown';
  }

  return path
    .split('/')
    .map((segment) => {
      if (/^[0-9a-f]{24}$/i.test(segment)) return ':id';
      if (/^\d+$/.test(segment)) return ':id';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
        return ':id';
      }
      return segment;
    })
    .join('/');
};

const theStatusThatCameBack = (status: unknown): string =>
  typeof status === 'number' && Number.isFinite(status) ? String(status) : 'unknown';

/**
 * Rewrites the event when it is an escaped HTTP failure, and returns it
 * unchanged when it is anything else.
 *
 * Never throws. A report that fails is a report lost, and this runs on the way
 * out of every single event in the application.
 */
export const anEscapedHttpFailureHasAName = (event: SentryErrorEvent): SentryErrorEvent => {
  try {
    const serialised = (event.extra as { __serialized__?: TheSerialisedFailure })?.__serialized__;
    if (!looksLikeAnHttpFailure(serialised)) {
      return event;
    }

    const status = theStatusThatCameBack(serialised?.status);
    const path = thePathThatFailed(serialised?.url);

    const failure = event.exception?.values?.[0];
    if (failure) {
      failure.type = 'HttpErrorResponse';
      failure.value = `${status} on ${path}`;
    }

    if (status === '0') {
      // No response at all: the phone's network, not an endpoint. Grouped by
      // endpoint, every screen grew its own issue for the same lost signal
      // (CAPACITOR-1 on my groups, -18 on the search, -1B on a group page).
      // One issue for all of them, as a warning: quiet day to day, and a
      // sudden spike, the shape a CORS or DNS break would take, still shows.
      return {
        ...event,
        level: 'warning',
        fingerprint: ['http-no-response'],
        tags: { ...event.tags, http_status: status, http_path: path },
      };
    }

    return {
      ...event,
      // By endpoint and status, never by the message: the message carries the
      // full URL, so grouping on it would split one broken endpoint across as
      // many issues as it has identifiers.
      fingerprint: ['http-failure', status, path],
      tags: { ...event.tags, http_status: status, http_path: path },
    };
  } catch {
    return event;
  }
};
